import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, FileText, Stethoscope } from "lucide-react";
import { http, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { Card, PageTitle, StatusPill, btnPrimary, btnGhost } from "@/components/portal/ui";

// My Clarity hub — status + routes to the existing intake/report pages (no functionality removed).
export default function MyClarity() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { http.get("/portal/overview").then((r) => {
    setData(r.data);
    const svcs = r.data.services || [];
    if (r.data.clarity_completed || svcs.some((s) => ["diagnostic", "guided", "inhome_overnight", "blueprint"].includes(s.product_key))) trackEvent("clarity_started", {});
  }).catch(() => setData({ services: [] })); }, []);
  if (!data) return <div className="text-[#6B665E]">Loading…</div>;

  const diag = (data.services || []).find((s) => s.product_key === "diagnostic");
  const clarityDone = data.clarity_completed;
  // Clarity access is included with Guided and In-Home + Overnight, not only the standalone Diagnostic.
  const owned = (data.services || []).some((s) => ["diagnostic", "guided", "inhome_overnight", "blueprint"].includes(s.product_key));

  // Unpaid: locked state. Primary CTA starts the existing Clarity checkout directly.
  if (!owned && !clarityDone) {
    return (
      <div>
        <PageTitle eyebrow="Your Journey" title="My Clarity" sub="Every family starts here — the Infant Sleep Diagnostic." />
        <Card className="p-8 text-center" data-testid="clarity-locked">
          <Stethoscope className="mx-auto text-[#9F634E]" size={40} />
          <p className="font-display text-2xl text-[#1A1918] mt-4">Start with the Infant Sleep Diagnostic</p>
          <p className="text-[#6B665E] mt-2 text-sm max-w-md mx-auto">Your Certified Infant Sleep Consultant reviews a focused intake and writes your personalized Clarity Report.</p>
          <button data-testid="clarity-start-checkout" onClick={() => startCheckout({ product: "diagnostic", user, navigate: nav })} className={`${btnPrimary} mt-6`}>Start With Clarity — $99</button>
          <div className="mt-4"><button data-testid="clarity-back-home" onClick={() => nav("/portal")} className="text-[#9F634E] text-sm hover:underline">Back to Home</button></div>
        </Card>
      </div>
    );
  }

  const st = diag?.state || (clarityDone ? "Completed" : "Not Started");
  const intakeDone = ["Submitted", "Awaiting Operator Review", "Under Review", "Delivered", "Viewed", "Completed"].includes(st) || clarityDone;
  const reportReady = ["Delivered", "Viewed", "Completed"].includes(st) || clarityDone;

  return (
    <div>
      <PageTitle eyebrow="Your Journey" title="My Clarity" sub="Your Infant Sleep Diagnostic — understand what's happening first." />
      <div className="flex items-center gap-3 mb-5"><span className="text-sm text-[#8A998C]">Current status:</span><StatusPill state={st} /></div>

      <div className="flex items-start gap-2 bg-[#667A66]/10 border border-[#667A66]/20 rounded-lg px-4 py-3 mb-5 max-w-2xl" data-testid="clarity-call-included-note">
        <CheckCircle2 size={16} className="text-[#4d6b4d] mt-0.5 shrink-0" />
        <p className="text-sm text-[#4d6b4d]">Your Clarity purchase includes a complimentary, optional 30-minute review call with Andrea Hurley — a one-time review, not ongoing coaching. The booking button appears {reportReady ? "with your report below." : "here once your Clarity Report is ready."}</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        <Card className="p-6 flex items-center justify-between gap-4" data-testid="clarity-intake-card">
          <div>
            <div className="flex items-center gap-2 text-[#8A998C] text-xs mb-1">{intakeDone ? <CheckCircle2 size={13} className="text-[#4d6b4d]" /> : <Clock size={13} />} Step 1 · Clarity Intake</div>
            <p className="font-display text-lg text-[#1A1918]">Your sleep intake</p>
            <p className="text-[#6B665E] text-sm">{intakeDone ? "Submitted for your Certified Infant Sleep Consultant to review." : "Complete your focused intake so your Certified Infant Sleep Consultant can begin."}</p>
          </div>
          {!intakeDone && <button data-testid="clarity-go-intake" onClick={() => nav("/portal/diagnostic")} className={`${btnPrimary} shrink-0 inline-flex items-center gap-2`}>{st === "In Progress" ? "Continue Clarity Intake" : "Begin Clarity Intake"} <ArrowRight size={15} /></button>}
        </Card>

        <Card className="p-6" data-testid="clarity-review-card">
          <div className="flex items-center gap-2 text-[#8A998C] text-xs mb-1">{reportReady ? <CheckCircle2 size={13} className="text-[#4d6b4d]" /> : intakeDone ? <Clock size={13} /> : <Clock size={13} className="opacity-40" />} Step 2 · Consultant Review</div>
          <p className="font-display text-lg text-[#1A1918]">Professional review</p>
          <p className="text-[#6B665E] text-sm">{reportReady ? "Complete — your report is ready below." : intakeDone ? "Your Certified Infant Sleep Consultant is reviewing your intake now." : "Begins once your intake is submitted."}</p>
        </Card>

        <Card className="p-6 flex items-center justify-between gap-4" data-testid="clarity-report-card">
          <div>
            <div className="flex items-center gap-2 text-[#8A998C] text-xs mb-1"><FileText size={13} /> Step 3 · Clarity Report</div>
            <p className="font-display text-lg text-[#1A1918]">Your Infant Sleep Clarity Report</p>
            <p className="text-[#6B665E] text-sm">{reportReady ? "Ready to view." : "Appears here once your Certified Infant Sleep Consultant completes the review."}</p>
          </div>
          {reportReady && <button data-testid="clarity-view-report" onClick={() => nav("/portal/report")} className={`${btnPrimary} shrink-0 inline-flex items-center gap-2`}>View Report <ArrowRight size={15} /></button>}
        </Card>

        {reportReady && (
          <Card className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div><p className="font-display text-lg text-[#1A1918]">Ready for the full plan?</p><p className="text-[#6B665E] text-sm mt-0.5">The Personalized Blueprint turns your Clarity priorities into a step-by-step strategy.</p></div>
            <button onClick={() => nav("/portal/my-blueprint")} className={`${btnGhost} shrink-0`}>Go to My Blueprint</button>
          </Card>
        )}
      </div>
    </div>
  );
}
